import type { CSSProperties } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from 'components/Icon';
import { lineStrings } from 'constants/strings';
import { ECardTypeTemplateId, TCardTypeCard } from 'types/cardTypeMessage';
import { getCardImages } from 'utils/cardTypeMessage';
import { cn } from 'utils/cn';
import { CardTypeMessagePreview } from './CardTypeMessagePreview';

const strings = lineStrings.management.cardTypeMessage;

export type TCardTypeSortableCard = {
  id: number;
  card: TCardTypeCard;
};

function getCardTitle(card: TCardTypeCard): string {
  return card.header?.trim() || card.name?.trim() || card.tag?.text.trim() || '';
}

export function getCardLabel(card: TCardTypeCard, index: number): string {
  const contentLabel = getCardTitle(card);
  return contentLabel
    ? `${strings.cardTab} ${index + 1}: ${contentLabel}`
    : `${strings.cardTab} ${index + 1}`;
}

function MobileCardSummary({
  item,
  index,
}: {
  item: TCardTypeSortableCard;
  index: number;
}) {
  const imageUrl = getCardImages(item.card).a;
  const title = getCardTitle(item.card) || `${strings.cardTab} ${index + 1}`;

  return (
    <>
      <span className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block text-xs font-medium text-neutral-500">
          {strings.cardTab} {index + 1}
        </span>
        <span className="mt-0.5 block truncate text-sm font-semibold text-neutral-900">
          {title}
        </span>
      </span>
    </>
  );
}

export function MobileDragOverlayCard({
  item,
  index,
}: {
  item: TCardTypeSortableCard;
  index: number;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-accent bg-white p-2 pr-3 shadow-xl">
      <MobileCardSummary item={item} index={index} />
      <span className="flex min-h-11 min-w-11 items-center justify-center text-accent">
        <Icon icon="menuBars" width={20} height={20} />
      </span>
    </div>
  );
}

type Props = {
  item: TCardTypeSortableCard;
  index: number;
  templateId: ECardTypeTemplateId;
  disabled: boolean;
  mobile: boolean;
  previewOpen: boolean;
  cardCount: number;
  onTogglePreview: () => void;
  onMoveToPosition: (position: number) => void;
};

export function CardTypeMessageSortableCard({
  item,
  index,
  templateId,
  disabled,
  mobile,
  previewOpen,
  cardCount,
  onTogglePreview,
  onMoveToPosition,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`reorder-card-${item.id}`}
      className={cn(
        'relative mx-auto flex w-full flex-col rounded-xl border bg-neutral-50 p-2 shadow-sm sm:max-w-[232px] sm:rounded-2xl',
        isDragging
          ? 'z-10 border-accent opacity-80 shadow-xl'
          : 'border-neutral-200',
      )}
      data-card-id={item.id}
    >
      {mobile ? (
        <>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-lg p-1 outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-accent/40"
              aria-label={`${getCardLabel(item.card, index)}: ${
                previewOpen
                  ? strings.reorderClosePreview
                  : strings.reorderPreview
              }`}
              aria-expanded={previewOpen}
              onClick={onTogglePreview}
            >
              <MobileCardSummary item={item} index={index} />
            </button>
            <select
              disabled={disabled}
              value={index + 1}
              className="min-h-11 w-[72px] shrink-0 rounded-lg border-neutral-200 bg-white py-1 pl-2 pr-7 text-xs font-semibold text-neutral-700 outline-none focus:border-accent focus:ring-2 focus:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`${getCardLabel(item.card, index)}: ${strings.reorderMoveTo}`}
              onChange={(event) =>
                onMoveToPosition(Number(event.target.value))
              }
            >
              {Array.from({ length: cardCount }, (_, positionIndex) => {
                const position = positionIndex + 1;
                return (
                  <option key={position} value={position}>
                    {position}
                    {strings.reorderPositionLabel}
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              disabled={disabled}
              className="flex min-h-11 min-w-11 touch-none items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-700 outline-none hover:border-accent/60 hover:text-accent focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`${getCardLabel(item.card, index)}: ${strings.reorderDragHandle}`}
              {...attributes}
              {...listeners}
            >
              <Icon icon="menuBars" width={20} height={20} />
            </button>
          </div>
          {previewOpen ? (
            <div className="mt-2 flex justify-center border-t border-neutral-200 px-2 pb-2 pt-4">
              <CardTypeMessagePreview
                templateId={templateId}
                card={item.card}
                lazyImages
              />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={disabled}
            className="mb-2 flex min-h-11 touch-none items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 outline-none transition hover:border-accent/60 hover:text-accent focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`${getCardLabel(item.card, index)}: ${strings.reorderDragHandle}`}
            {...attributes}
            {...listeners}
          >
            <Icon icon="menuBars" width={18} height={18} />
            <span>
              {index + 1}
              {strings.reorderPositionLabel}
            </span>
          </button>
          <div
            className="pointer-events-none flex justify-center"
            aria-hidden="true"
          >
            <CardTypeMessagePreview
              templateId={templateId}
              card={item.card}
              variant="inline"
              lazyImages
            />
          </div>
        </>
      )}
    </li>
  );
}
